import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RegistrationForm } from '../components/forms/RegistrationForm';
import { Background } from '../components/layout/Background';
import { BackgroundLeftText } from '../components/layout/BackgroundLeftText';
import { useAppDispatch } from '../hooks/redux';
import { resetAuthState } from '../store/slices/authSlice';
import { showToast } from '../store/slices/toastSlice';
import { PATHS } from '../constants/Paths.constants';
import { validateInvitation } from '../services/TeamService';
import Loader from '../components/ui/Loader/Loader';

export function RegistrationPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const tokenFromUrl = new URLSearchParams(location.search).get('token') || '';
  const emailFromUrl = new URLSearchParams(location.search).get('email') || '';
  const isInvitationFlow = Boolean(tokenFromUrl && emailFromUrl);

  const [validatingToken, setValidatingToken] = useState(true);

  useEffect(() => {
    dispatch(resetAuthState());
  }, [dispatch]);

  useEffect(() => {
    if (!tokenFromUrl || !emailFromUrl) {
      setValidatingToken(false);
      return;
    }

    const validateInvitationToken = async () => {
      setValidatingToken(true);

      try {
        const response = await validateInvitation(tokenFromUrl);

        if (!response.data.valid) {
          if (response.data.errorCode === 'TEAM_INVITATION_ALREADY_ACCEPTED') {
            dispatch(
              showToast({
                message: 'Invitation already accepted',
                type: 'error'
              })
            );
            navigate(PATHS.LOGIN, { replace: true });
            return;
          }

          navigate(PATHS.EXPIRED_LINK, {
            replace: true,
            state: { from: 'invitation' }
          });
          return;
        }

        setValidatingToken(false);
      } catch (error: any) {
        if (
          error?.response?.data?.errorCode ===
          'TEAM_INVITATION_ALREADY_ACCEPTED'
        ) {
          dispatch(
            showToast({
              message: 'Invitation already accepted',
              type: 'error'
            })
          );
          navigate(PATHS.LOGIN, { replace: true });
          return;
        }

        navigate(PATHS.EXPIRED_LINK, {
          replace: true,
          state: { from: 'invitation' }
        });
      }
    };

    validateInvitationToken();
  }, [tokenFromUrl, emailFromUrl, navigate, dispatch]);

  return (
    <div className="min-w-screen min-h-screen relative flex flex-col md:flex-row justify-between items-center">
      <Background />
      {validatingToken ? (
        <div className="flex w-screen h-screen justify-center items-center">
          <Loader />
        </div>
      ) : (
        <>
          <BackgroundLeftText />
          <div className="w-full md:w-1/2 p-4 flex flex-col items-center justify-center">
            <div className="w-full max-w-[458px] py-8 px-10 rounded-2xl bg-white shadow-sm border border-gray-200">
              <p className="text-2xl text-purple-900 font-bold text-left mb-6">
                Create your account
              </p>
              <RegistrationForm
                invitation={isInvitationFlow}
                invitationToken={tokenFromUrl}
                invitationEmail={emailFromUrl}
              />
              <p className="mt-6 text-xs font-normal text-gray-500 text-left tracking-wide">
                By clicking ‘Create Account’ you agree to accept our {''}
                <a href={`${PATHS.TERMSANDPRIVACY}?tab=terms`}>
                  <span className="underline-custom">Terms of</span> <br />
                  <span className="underline-custom">Service</span>
                </a>
                {''} and {''}
                <a href={`${PATHS.TERMSANDPRIVACY}?tab=policy`}>
                  <span className="underline-custom">Privacy Policy</span>.
                </a>{' '}
                {''}
                You can always access these <br /> documents later in the app
                main menu.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
